import { EllipsisMenu, FusionInputField } from 'components/index';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import useSearch from 'hooks/useSearch';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { FaEye, FaSave } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import Popup from 'reactjs-popup';
import {
  getOrgName,
  getWorkplaceMembers,
  updateOrgName,
} from 'services/organization/organization.service';
import { getAllRoles } from 'services/roles/roles.service';
import { deleteUser } from 'services/user/user.service';
import { getVisibleUsers } from 'utils/roleUtils';
import { urlAppendParam } from 'utils/tabsHeaderUtils';

export default function MembersTab({
  list,
  perms,
  setMemberList,
  onCreateUser,
  listOnly,
  editableOrgName,
}) {
  const search = useSearch();
  const [showConfirmDeleteMember, setShowConfirmDeleteMember] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [editOrgName, setEditOrgName] = useState(false);
  const [hierarchy, setHierarchy] = useState({});
  const [roleList, setRoleList] = useState([]);
  const [yourHighestRoleHierarchyId, setYourHighestRoleHierarchyId] = useState(null);
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});

  useEffect(() => {
    (async () => {
      await getAllRoles().then((res) => {
        setRoleList([...res.data.result]);
      });

      if (editableOrgName)
        await getOrgName().then((res) => {
          setOrgName(res.data.result.work_place_name);
        });

      await getWorkplaceMembers(getVisibleUsers(perms)).then((res) => {
        setMemberList([...res.data.result]);
      });
    })();
  }, []);

  useEffect(() => {
    const rolesToHierarch = roleList.filter((item) => {
      return parseInt(item.role_Id) >= 0;
    });
    let hierarchy = {};
    rolesToHierarch.forEach((role) => {
      hierarchy[role.role_Id] = role.hierarchy_level;
    });
    let max = 999;

    let l = roleList;
    const myRoles = l
      .filter((item) => {
        if (item.users?.includes(Cookies.get('username'))) {
          return item.role_Id;
        }
      })
      .map((item) => {
        return item.role_Id;
      });

    rolesToHierarch.forEach((role) => {
      if (myRoles.includes(role.role_Id)) {
        if (role.hierarchy_level <= max) {
          max = role.hierarchy_level;
        }
      } else {
      }
    });
    setYourHighestRoleHierarchyId(max);

    setHierarchy(hierarchy);
  }, [roleList]);

  const localUpdateOrgName = async () => {
    await updateOrgName(orgName)
      .then((res) => {
        Cookies.set('organizationName', orgName);
        setEditOrgName(false);
      })
      .catch((err) => {});
  };

  const searchTest = useCallback(
    (item) =>
      item.name.toLowerCase().replace(/ /g, '').includes(search.toLowerCase().replace(/ /g, '')) ||
      item.role_name
        .toLowerCase()
        .replace(/ /g, '')
        .includes(search.toLowerCase().replace(/ /g, '')) ||
      item.email.toLowerCase().replace(/ /g, '').includes(search.toLowerCase().replace(/ /g, '')) ||
      item.create_by_email
        .toLowerCase()
        .replace(/ /g, '')
        .includes(search.toLowerCase().replace(/ /g, '')),
    [search],
  );

  const getRoleHierarchy = useCallback(
    (roles) => {
      let highestHierarchyRole = 999;
      JSON.parse(roles)?.forEach((role) => {
        if (role < 0) return;
        if (hierarchy[role] <= highestHierarchyRole) {
          highestHierarchyRole = hierarchy[role];
        }
      });

      if (perms.accessLevel === 8) {
        return false;
      } else {
        return highestHierarchyRole <= yourHighestRoleHierarchyId;
      }
    },
    [yourHighestRoleHierarchyId, hierarchy, perms],
  );

  const filterUserRoles = useCallback((roles) => {
    return roles.filter((item) => {
      return item !== -2;
    });
  }, []);

  const sortedList = useMemo(() => {
    if (list.length === 0) return [];
    return list
      .sort((a, b) => {
        return new Date(b.date_added) - new Date(a.date_added);
      })
      .sort((a, b) => {
        return Cookies.get('username') === a.email ? -1 : 1;
      })
      .map((item) => {
        if (!searchTest(item)) return null;
        return item;
      });
  }, [list, searchTest]);

  const columns = useMemo(
    () => [
      {
        header: 'Users',
        accessorKey: 'name',
        Cell: ({ row, cell }) => {
          const activated = row.original.activated;
          const name = cell.getValue();
          return (
            <div className="d-flex gap-2 fw-bold fs-6">
              {activated === 0 && <p className="text-danger">{'(pending)'}</p>}
              <p className="text-wrap text-break text-white">{name}</p>
            </div>
          );
        },
        size: 200,
      },
      {
        header: 'Roles',
        accessorKey: 'role_name',
        Cell: ({ row, cell }) => {
          const roles = cell.getValue();
          const parsedRoles = JSON.parse(roles);

          return roles ? (
            filterUserRoles(parsedRoles).length > 1 ? (
              <Popup
                trigger={<p className="text-wrap text-white fs-6">Multiple Roles</p>}
                position="top center"
                on="hover"
                closeOnDocumentClick
              >
                <div className="bg-primary border border-secondary rounded">
                  {roleList.length > 0 &&
                    parsedRoles
                      .filter((item) => {
                        return item !== -2;
                      })
                      .map((role, index) => {
                        return (
                          <p key={index} className="p-1 mb-0">
                            {/* {role} */}
                            {roleList.find((roleItem) => roleItem.role_Id === role)
                              ? roleList.find((roleItem) => roleItem.role_Id === role).role_name
                              : 'missing?'}
                          </p>
                        );
                      })}
                </div>
              </Popup>
            ) : (
              <p className="text-wrap text-white fs-6">
                {parsedRoles.length > 1
                  ? roleList.find(
                      (roleItem) => roleItem.role_Id === filterUserRoles(parsedRoles)[0],
                    )?.role_name
                  : roleList.find((roleItem) => roleItem.role_Id === parsedRoles[0])
                    ? roleList.find((roleItem) => roleItem.role_Id === parsedRoles[0]).role_name
                    : 'missing role_Id'}
              </p>
            )
          ) : (
            <p className="text-wrap text-white fs-6">{roles}</p>
          );
        },
      },
      {
        header: 'Email',
        accessorKey: 'email',
        Cell: ({ row, cell }) => {
          const email = cell.getValue();
          return (
            <p className="text-wrap text-break text-white fs-6" style={{ maxWidth: '200px' }}>
              {email}
            </p>
          );
        },
        size: 200,
      },
      {
        header: 'Created By',
        accessorKey: 'create_by_email',
        Cell: ({ row, cell }) => {
          const create_by_email = cell.getValue();
          return (
            <p className="text-wrap text-break text-white fs-6" style={{ maxWidth: '200px' }}>
              {create_by_email}
            </p>
          );
        },
        size: 200,
      },
      {
        header: 'Date Created',
        accessorKey: 'date_added',
        Cell: ({ row, cell }) => {
          const date_added = cell.getValue();
          return <p className="text-white fs-6">{date_added}</p>;
        },
      },
      {
        header: '',
        accessorKey: 'actions',
        Cell: ({ row, cell }) => {
          const sharedHierarchyOrHigher = row.original.sharedHierarchyOrHigher;
          const userId = row.original.userId;
          const perm_level = row.original.perm_level;
          const email = row.original.email;

          return (
            <div key={userId} className="py-2 text-white">
              {((perms.accessLevel === 12 && perm_level === 12) ||
                (sharedHierarchyOrHigher && perms.accessLevel !== 12 && perms.accessLevel !== 13) ||
                !perms.testPermission([8, 10, 11, 12, 13], ['Create-EditUsers'])) && (
                <Button
                  as={Link}
                  to={`/system/viewuser?userid=${userId}`}
                  className="rounded-circle py-1 px-2"
                  variant="secondary"
                >
                  <FaEye color="white" />
                </Button>
              )}

              {Cookies.get('username') !== email &&
                (perms.accessLevel === 8 ||
                  (perms.accessLevel === 13 && perm_level <= 12) ||
                  (perms.accessLevel === 12 && perm_level <= 10) ||
                  (perms.accessLevel === 10 && perm_level <= 10)) &&
                (!sharedHierarchyOrHigher ||
                  perms.accessLevel === 13 ||
                  perms.accessLevel === 12) &&
                perms.testPermission([8, 10, 11, 12, 13], ['Create-EditUsers']) && (
                  <EllipsisMenu
                    options={[
                      {
                        component: (
                          <>
                            {perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole']) && (
                              <Button
                                as={Link}
                                to={`/system/edituser?userid=${userId}`}
                                className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                variant="primary"
                              >
                                Edit
                              </Button>
                            )}
                          </>
                        ),
                      },
                      {
                        component: (
                          <>
                            {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteRole']) && (
                              <Button
                                className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                onClick={() => {
                                  setSelectedMember(row.original);
                                  setShowConfirmDeleteMember(true);
                                }}
                                variant="danger"
                              >
                                Remove
                              </Button>
                            )}
                          </>
                        ),
                      },
                    ]}
                    open={rowIdEllipsisOpen[userId]}
                    containerClassname="bg-transparent"
                    onOpen={() =>
                      setRowIdEllipsisOpen({
                        ...rowIdEllipsisOpen,
                        [userId]: true,
                      })
                    }
                    onClose={() =>
                      setRowIdEllipsisOpen({
                        ...rowIdEllipsisOpen,
                        [userId]: false,
                      })
                    }
                  />
                )}
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [roleList, perms, rowIdEllipsisOpen, filterUserRoles],
  );

  const rows = useMemo(() => {
    if (sortedList.length === 0) return [];

    const cleanedSearch = search.toLowerCase().replace(/ /g, '');

    const filteredList = sortedList.filter((user) => {
      const name = user?.name?.toLowerCase()?.replace(/ /g, '');
      const roles = user?.roles?.toLowerCase()?.replace(/ /g, '');
      const email = user?.email?.toLowerCase()?.replace(/ /g, '');
      const create_by_email = user?.create_by_email?.toLowerCase()?.replace(/ /g, '');
      return (
        name?.includes(cleanedSearch) ||
        roles?.includes(cleanedSearch) ||
        email?.includes(cleanedSearch) ||
        create_by_email?.includes(cleanedSearch)
      );
    });

    return filteredList.map((user) => ({
      userId: user.userId,
      activated: user.activated,
      name: user.name,
      role_name: user.roles,
      email: user.email,
      create_by_email: user.create_by_email,
      date_added: new Date(user.date_added).toLocaleString([], {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      }),
      perm_level: user.perm_level,
      sharedHierarchyOrHigher: getRoleHierarchy(user.roles),
    }));
  }, [sortedList, getRoleHierarchy, search]);

  return (
    <Container
      fluid
      className="mt-3 pb-3 d-flex flex-column flex-grow-1 h-100 min-h-0 border-box"
      style={{ boxSizing: 'border-box' }}
    >
      {showConfirmDeleteMember && (
        <ConfirmationModal
          show={showConfirmDeleteMember}
          handleShow={setShowConfirmDeleteMember}
          bodyTxt={`Are you sure you want to remove ${selectedMember.name} from the workplace?`}
          cancelBtnTxt="Cancel"
          confirmBtnTxt="Yes"
          confirmHandler={async () => {
            await deleteUser(selectedMember.userId);
            setSelectedMember(null);
            setShowConfirmDeleteMember(false);
            await getWorkplaceMembers([9, 10, 12, 13]).then((res) => {
              setMemberList([...res.data.result]);
            });
          }}
          cancelHandler={() => {
            setShowConfirmDeleteMember(false);
            setSelectedMember(null);
          }}
        />
      )}

      <div className="d-flex flex-column flex-md-row align-items-center gap-2 justify-content-between mb-3">
        {editableOrgName && (
          <div className="d-flex gap-1">
            <FusionInputField
              disabled={!editOrgName}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              includeClear={false}
            />
            {editOrgName ? (
              <Button
                onClick={() => {
                  localUpdateOrgName();
                }}
              >
                <FaSave />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setEditOrgName(true);
                }}
              >
                <FaPencil />
              </Button>
            )}
          </div>
        )}
        <div className="ms-md-auto">
          <SearchBar className="bg-primary-highlight rounded-3" />
        </div>
      </div>
      {list.length > 0 ? (
        <div className="w-100 flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
          <BaseTable
            columns={columns}
            rows={rows}
            customStyles={{
              container: { height: '100%' },
              cell: { borderBottom: '3px solid #E0E1E228' },
              header: { borderBottom: '3px solid #E0E1E228' },
            }}
            muiTablePaperProps={{
              sx: { height: '100%', display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
          />
        </div>
      ) : (
        <WidgetLoadingAnimation />
      )}
      {!listOnly && perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditUsers']) && (
        <div className="d-flex flex-row justify-content-end">
          <Button
            as={Link}
            to={urlAppendParam('tab', 'Create New User')}
            className="p-3 w-100 mt-3 w-md-auto"
            onClick={onCreateUser}
            variant="primary"
          >
            Create New User
          </Button>
        </div>
      )}
    </Container>
  );
}
